import { useCallback, useEffect, useState } from "react";
import { apiUrl } from "utils/apiUrl";

const STORAGE_KEY = "alphasniper_copy_service_stats_v2";
const LEGACY_STORAGE_KEY = atob("YWxwaGFzbmlwZXJfZHVwbGlrdW1fc3RhdHNfdjI=");

function readCache() {
  for (const k of [STORAGE_KEY, LEGACY_STORAGE_KEY]) {
    try {
      const raw = sessionStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed.masters === "number" &&
        typeof parsed.slavesActive === "number" &&
        typeof parsed.slavesInactive === "number"
      ) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
  }
  return null;
}

function writeCache(data) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

function display(n) {
  return typeof n === "number" ? String(n) : "—";
}

function isCopyApiAccessDisabled(message) {
  if (!message || typeof message !== "string") return false;
  const m = message.toLowerCase();
  return (
    (m.includes("api access") && m.includes("disabled")) ||
    m.includes("switch it on")
  );
}

function shouldShowCopyServiceEnvHelp(message) {
  if (!message || typeof message !== "string") return false;
  const m = message.toLowerCase();
  return (
    m.includes("basic auth") ||
    m.includes("1002") ||
    m.includes("not configured") ||
    m.includes("trade copier service is not configured")
  );
}

function SimpleStatCard({ title, value, hint }) {
  return (
    <div className="flex min-h-[100px] min-w-0 flex-1 flex-col justify-center rounded-xl border border-gray-200 bg-white/60 px-4 py-3 dark:border-white/10 dark:bg-navy-800/60">
      <p className="text-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {title}
      </p>
      <p className="mt-2 text-center text-3xl font-bold tabular-nums text-navy-700 dark:text-white">
        {value}
      </p>
      {hint ? (
        <p className="mt-1 text-center text-[10px] text-gray-400 dark:text-gray-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function SlavesSplitCard({ active, inactive }) {
  return (
    <div className="flex min-h-[100px] min-w-0 flex-[1.15] flex-col rounded-xl border border-gray-200 bg-white/60 dark:border-white/10 dark:bg-navy-800/60">
      <p className="border-b border-gray-200 py-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-white/10 dark:text-gray-400">
        Slaves
      </p>
      <div className="grid flex-1 grid-cols-2 divide-x divide-gray-200 dark:divide-white/10">
        <div className="flex flex-col justify-center px-2 py-3 sm:px-4">
          <p className="text-center text-xs font-medium text-gray-500 dark:text-gray-400">
            Active
          </p>
          <p className="mt-1 text-center text-2xl font-bold tabular-nums text-navy-700 dark:text-white sm:text-3xl">
            {active}
          </p>
        </div>
        <div className="flex flex-col justify-center px-2 py-3 sm:px-4">
          <p className="text-center text-xs font-medium text-gray-500 dark:text-gray-400">
            Inactive
          </p>
          <p className="mt-1 text-center text-2xl font-bold tabular-nums text-navy-700 dark:text-white sm:text-3xl">
            {inactive}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function AdminCopyServiceStatsBar() {
  const initial = readCache();
  const [masters, setMasters] = useState(
    typeof initial?.masters === "number" ? initial.masters : null,
  );
  const [slavesActive, setSlavesActive] = useState(
    typeof initial?.slavesActive === "number" ? initial.slavesActive : null,
  );
  const [slavesInactive, setSlavesInactive] = useState(
    typeof initial?.slavesInactive === "number" ? initial.slavesInactive : null,
  );
  const [followers, setFollowers] = useState(
    typeof initial?.followers === "number" ? initial.followers : null,
  );
  const [error, setError] = useState(null);

  const applyPayload = useCallback((data) => {
    setMasters(data.masters);
    setSlavesActive(data.slavesActive);
    setSlavesInactive(data.slavesInactive);
    setFollowers(
      typeof data.followers === "number" ? data.followers : null,
    );
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch(apiUrl("/api/copy-service/stats"));
      const data = await res.json().catch(() => ({}));

      const statsOk =
        data.ok &&
        typeof data.masters === "number" &&
        typeof data.slavesActive === "number" &&
        typeof data.slavesInactive === "number";

      if (statsOk) {
        applyPayload(data);
        setError(null);
        writeCache({
          masters: data.masters,
          slavesActive: data.slavesActive,
          slavesInactive: data.slavesInactive,
          followers:
            typeof data.followers === "number" ? data.followers : null,
        });
        return;
      }

      if (typeof data.followers === "number") {
        setFollowers(data.followers);
      }

      setError(data.message || "Unable to load stats.");
      const c = readCache();
      if (!c) {
        setMasters(null);
        setSlavesActive(null);
        setSlavesInactive(null);
        setFollowers(null);
      }
    } catch {
      setError("Unable to reach the server.");
      const c = readCache();
      if (!c) {
        setMasters(null);
        setSlavesActive(null);
        setSlavesInactive(null);
        setFollowers(null);
      }
    }
  }, [applyPayload]);

  useEffect(() => {
    load();
    const id = setInterval(load, 120000);
    const onVis = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  return (
    <div className="w-full">
      <div className="rounded-xl border border-gray-200 bg-white/90 shadow-md shadow-shadow-500 backdrop-blur-md dark:border-white/10 dark:bg-navy-800/95 dark:shadow-none">
        <div className="flex flex-col gap-2 border-b border-gray-100 px-3 py-2 dark:border-white/10">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <p className="text-center text-sm font-bold text-navy-700 dark:text-white sm:text-left">
              Statistics
            </p>
            {error ? (
              <p
                className="max-w-full text-center text-xs text-amber-600 dark:text-amber-400 sm:text-right"
                title={error}
              >
                {error}
              </p>
            ) : null}
          </div>
          {error && isCopyApiAccessDisabled(error) ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100">
              <p className="font-semibold">Pour afficher Master et Slaves</p>
              <ol className="mt-1 list-decimal pl-4">
                <li>
                  Va sur le{" "}
                  <a
                    href="https://www.trade-copier.com/cockpit"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline"
                  >
                    cockpit du copieur
                  </a>{" "}
                  (même compte que dans ton{" "}
                  <code className="rounded bg-white/60 px-1 dark:bg-black/30">.env</code>).
                </li>
                <li>
                  Ouvre le menu de ton profil (en haut à droite) → réglages liés à{" "}
                  <strong>Trade Copier API</strong> (ou « API ») et{" "}
                  <strong>active l’accès API</strong> (interrupteur ON).
                </li>
                <li>
                  Redémarre l’API locale (
                  <code className="rounded bg-white/60 px-1 dark:bg-black/30">
                    npm run dev
                  </code>
                  ) puis recharge cette page.
                </li>
              </ol>
              <p className="mt-2 text-[11px] opacity-90">
                Tant que l’accès API est désactivé côté fournisseur, le site ne peut pas lire tes comptes — le cockpit peut toujours les afficher.
              </p>
            </div>
          ) : null}
          {error &&
          !isCopyApiAccessDisabled(error) &&
          shouldShowCopyServiceEnvHelp(error) ? (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs leading-relaxed text-sky-950 dark:border-sky-500/40 dark:bg-sky-950/30 dark:text-sky-100">
              <p className="font-semibold">Erreur « Basic Auth » ou 1002 dans le navigateur ?</p>
              <p className="mt-1">
                Ouvrir{" "}
                <code className="rounded bg-white/70 px-1 dark:bg-black/40">
                  getToken.php
                </code>{" "}
                dans la barre d’adresse envoie une requête <strong>sans</strong> identifiants — le
                service répond <code className="rounded bg-white/70 px-1 dark:bg-black/40">1002</code>{" "}
                : <strong>c’est normal</strong>, ce n’est pas un test valide.
              </p>
              <p className="mt-2 font-semibold">Pour que le dashboard affiche Master / Slaves</p>
              <ol className="mt-1 list-decimal pl-4">
                <li>
                  Dans le dossier du projet, fichier{" "}
                  <code className="rounded bg-white/70 px-1 dark:bg-black/40">.env</code> (à côté de{" "}
                  <code className="rounded bg-white/70 px-1 dark:bg-black/40">package.json</code>
                  ), ajoute les identifiants du connecteur de copie décrits dans{" "}
                  <code className="rounded bg-white/70 px-1 dark:bg-black/40">.env.example</code>{" "}
                  (email + mot de passe du cockpit, sans guillemets si possible).
                </li>
                <li>
                  Enregistre le fichier, puis <strong>redémarre</strong> l’API (arrête et relance{" "}
                  <code className="rounded bg-white/70 px-1 dark:bg-black/40">npm run dev</code>).
                </li>
                <li>
                  Contrôle rapide : dans le navigateur, ouvre{" "}
                  <a
                    href={apiUrl("/api/copy-service/ping")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline"
                  >
                    {apiUrl("/api/copy-service/ping")}
                  </a>{" "}
                  — <code className="rounded bg-white/70 px-1 dark:bg-black/40">
                    credentialsConfigured
                  </code>{" "}
                  doit être <code className="rounded bg-white/70 px-1 dark:bg-black/40">true</code> et
                  les longueurs &gt; 0.
                </li>
              </ol>
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-stretch sm:gap-4">
          <SimpleStatCard
            title="Master"
            value={display(masters)}
            hint="Copier-side accounts"
          />
          <SimpleStatCard
            title="Followers"
            value={display(followers)}
            hint="Registered clients on the site"
          />
          <SlavesSplitCard
            active={display(slavesActive)}
            inactive={display(slavesInactive)}
          />
        </div>
      </div>
    </div>
  );
}
